import { useContext, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Spin } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { FilterFilled, HolderOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import EnhancedTable from 'app/molecules/EnhancedTable';
import { EnhancedColumnType } from 'app/molecules/EnhancedTable/types';
import { Button, StatusBadge } from 'app/atoms';
import { NavBreadcrumbContext } from 'app/molecules/NavBreadcrumb/NavBreadcrumbContext';
import { getContentItems, reorderContentItems } from './service';
import { ContentItem, ContentItemStatus } from './types';
import ContentItemForm from './ContentItemForm';
import EmptyState from './EmptyState';
import DraggableRow from './DraggableRow';

const ContentItems = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [isSorted, setIsSorted] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const { setItems } = useContext(NavBreadcrumbContext);
  const queryClient = useQueryClient();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    setItems([{ title: 'Dashboard', path: '/' }, { title: 'Onboarding Screens' }]);
  }, [setItems]);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['content-items'],
    queryFn: getContentItems,
  });

  const reorderMutation = useMutation({
    mutationFn: reorderContentItems,
    onSuccess: (reordered) => queryClient.setQueryData(['content-items'], reordered),
    onError: () => {
      toast.error('Failed to save the new order');
      queryClient.invalidateQueries({ queryKey: ['content-items'] });
    },
  });

  const filteredItems = items.filter((item) => !filters.status || item.status === filters.status);
  const canReorder = !filters.status && !isSorted;

  const handleTableChange: TableProps<ContentItem>['onChange'] = (_pagination, _filters, sorter) => {
    const active = Array.isArray(sorter) ? sorter.some((s) => s.order) : Boolean(sorter.order);
    setIsSorted(active);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(items, oldIndex, newIndex);
    queryClient.setQueryData(['content-items'], reordered);
    reorderMutation.mutate(reordered.map((item) => item.id));
  };

  const openCreate = () => {
    setSelectedItem(null);
    setIsFormOpen(true);
  };

  const openEdit = (item: ContentItem) => {
    setSelectedItem(item);
    setIsFormOpen(true);
  };

  const columns: EnhancedColumnType<ContentItem>[] = [
    ...(canReorder
      ? [
          {
            title: '',
            key: 'drag-handle',
            width: 40,
            render: () => <HolderOutlined className="text-gray-400" />,
          } as EnhancedColumnType<ContentItem>,
        ]
      : []),
    {
      title: 'Heading',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.title.localeCompare(b.title),
      className: 'font-figtree',
    },
    {
      title: 'Images',
      dataIndex: 'imageUrls',
      key: 'imageUrls',
      render: (urls: string[]) => (urls?.length ? `${urls.length} image${urls.length > 1 ? 's' : ''}` : '-'),
      className: 'font-figtree',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: ContentItemStatus) => (
        <StatusBadge status={status} label={status === ContentItemStatus.PUBLISHED ? 'Published' : 'Draft'} />
      ),
      filterOptions: {
        type: 'radio',
        Icon: FilterFilled,
        defaultValue: filters.status,
        options: [
          { label: 'Draft', value: ContentItemStatus.DRAFT },
          { label: 'Published', value: ContentItemStatus.PUBLISHED },
        ],
      },
    },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value: string) => dayjs(value).format('MMM D, YYYY HH:mm'),
      sorter: (a, b) => a.updatedAt.localeCompare(b.updatedAt),
      className: 'font-figtree whitespace-nowrap',
    },
  ];

  return (
    <div className="mx-auto max-2xl:max-w-[calc(100vw-180px)] max-sm:max-w-[calc(100vw-120px)] max-sm:px-0">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold font-figtree">Onboarding Screens</h2>
          {!canReorder && items.length > 1 && (
            <p className="text-sm text-gray-500 font-figtree flex items-center gap-1 mt-1">
              <InfoCircleOutlined /> Clear filters and sorting to drag and drop to reorder screens
            </p>
          )}
        </div>
        <Button variant="primary" onClick={openCreate}>
          New screen
        </Button>
      </div>

      <Spin spinning={isLoading}>
        {!isLoading && items.length === 0 ? (
          <EmptyState onCreate={openCreate} />
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <SortableContext
                items={canReorder ? filteredItems.map((item) => item.id) : []}
                strategy={verticalListSortingStrategy}
                disabled={!canReorder}
              >
                <EnhancedTable
                  dataSource={filteredItems}
                  columns={columns}
                  rowKey="id"
                  onFilter={(dataIndex, value) =>
                    setFilters((prev) => ({ ...prev, [String(dataIndex)]: String(value) }))
                  }
                  onChange={handleTableChange}
                  showEdit
                  showView={false}
                  showRemove={false}
                  onEdit={(_, item) => openEdit(item)}
                  onRow={(record) => ({ onClick: () => openEdit(record as ContentItem) })}
                  className="font-figtree"
                  components={canReorder ? { body: { row: DraggableRow } } : undefined}
                />
              </SortableContext>
            </DndContext>
          </div>
        )}
      </Spin>

      <ContentItemForm visible={isFormOpen} item={selectedItem} onClose={() => setIsFormOpen(false)} />
    </div>
  );
};

export default ContentItems;
